// angular
import {
  Component,
  inject,
  input,
  OnChanges,
  output,
} from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
// components
import { CheckBoxComponent } from '../checkbox/checkbox.component';
// types & utils
import { NestedFilter } from '@app/shared/types/nested-filter.type';
import { arrayToObject } from '@app/shared/utils/arrayToObject';

@Component({
  selector: 'pwj-nested-filters-box',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    CheckBoxComponent,
  ],
  templateUrl: './nested-filters-box.component.html',
  styleUrl: './nested-filters-box.component.scss',
})
export class NestedFiltersBoxComponent implements OnChanges {
  public isBoxOpened: boolean = false;
  public nestedFiltersForm!: FormGroup;
  public currentParams!: Params;
  public currentFilters!: any;

  // inputs
  boxTitle = input.required<string>();
  boxIcon = input.required<string>();
  isClosableBox = input<boolean>(true);
  nestedFilterItems = input.required<NestedFilter[]>();
  filterPrefix = input.required<string>();
  filterSuffix = input<string>('');
  filterSuffixColor = input<string>();

  // outputs
  onApplyFilters = output<any>();

  // dependencies
  private readonly _fb = inject(FormBuilder);
  private readonly _activatedRoute = inject(ActivatedRoute);

  constructor() {
    // initialize form fields
    this.nestedFiltersForm = this._fb.group({});
  }

  ngOnChanges(): void {
    if (this.nestedFilterItems()) {
      this.implementFormFields();

      // get last applied filters
      this._activatedRoute.queryParams.forEach(params => {
        this.currentParams = params;
        // TODO : optimization this code in future
        this.nestedFilterItems()?.map(filterItem => {
          if (params[filterItem.filterQueryName]) {
            this.currentParams = params;
          }
          const filtersInParams =
            this.currentParams[filterItem.filterQueryName];
          if (filtersInParams && Object.keys(filtersInParams)?.length > 0) {
            this.currentFilters = Object.keys(this.currentParams).map(key => {
              const paramValue = this.currentParams?.[key]?.split(',');
              const filterValue = Object.assign(
                paramValue.map((k: string) => ({ [k]: true }))
              );
              return {
                [key]: arrayToObject(filterValue),
              };
            });
            this.currentFilters = arrayToObject(this.currentFilters);
            this.updateFormFields();
          }
        });

        this.boxAlreadyOpen();
      });
    }
  }

  public implementFormFields() {
    this.nestedFilterItems()?.map((filters: any) => {
      // create form controls
      let filterItems = filters.items
        ?.map((item: string) => {
          return { [item]: new FormControl('') };
        })
        .reduce((acc: any, obj: any, index: number) => {
          const key = Object.keys(obj)[0];
          acc[key] = Object.values(obj)[0];
          return acc;
        }, {});

      this.nestedFiltersForm.addControl(
        filters.filterQueryName,
        new FormGroup({ ...filterItems })
      );
    });
  }

  public updateFormFields() {
    // get form field value
    this.nestedFiltersForm.patchValue({
      ...this.currentFilters,
    });
  }

  // apply filters functionality
  public setFiltersInParams() {
    this.nestedFiltersForm.valueChanges.subscribe(fields => {
      const filters = Object.keys(fields)
        .map(groupKey => {
          const groupItems = Object.keys(fields[groupKey])
            .map(itemKey => {
              return fields[groupKey][itemKey] ? itemKey : null;
            })
            .filter(item => item)
            .join(',');

          return groupItems?.length > 0
            ? { [groupKey]: groupItems }
            : { [groupKey]: null };
        })
        .filter(item => item)
        .reduce((acc: any, obj: any) => {
          const key = Object.keys(obj)[0];
          acc[key] = Object.values(obj)[0];
          return acc;
        }, {});

      // pass filters to apply filter function
      this.onApplyFilters.emit(filters);
    });
  }

  // box functionalities
  public setIsBoxOpened() {
    this.isBoxOpened = !this.isBoxOpened;
  }

  public boxAlreadyOpen() {
    this.nestedFilterItems().map(item => {
      if (
        this.currentFilters &&
        Object.keys(this.currentFilters)?.includes(item.filterQueryName)
      ) {
        this.isBoxOpened = true;
      }
    });
  }
}
